import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { useAuth } from './AuthContext';
import { useTCoins } from './TCoinsContext';
import { awardRewards, getUserGamificationStats, ACTIVITY_TYPES } from '../utils/gamificationManager';
import { getMascotByXP } from '../data/nurseAppModels';
import { getUserBadges } from '../utils/badgeManager';
import { getUserAchievements } from '../utils/achievementManager';

const GamificationContext = createContext();

export const useGamification = () => {
  const context = useContext(GamificationContext);
  if (!context) {
    throw new Error('useGamification must be used within a GamificationProvider');
  }
  return context;
};

export const GamificationProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const { addTCoins } = useTCoins();
  const [levelUpInfo, setLevelUpInfo] = useState(null);
  const [stats, setStats] = useState({
    totalPoints: 0,
    currentXP: 0,
    totalXP: 0,
    level: 1,
    mascot: null,
    stats: {}
  });
  const [badges, setBadges] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Award rewards for an activity
   * @param {string} activityType - Activity type (from ACTIVITY_TYPES)
   * @param {object} metadata - Additional metadata
   * @returns {Promise<object>} - Result
   */
  const awardActivity = async (activityType, metadata = {}) => {
    if (!currentUser?.uid) {
      return { success: false, error: 'User not logged in' };
    }

    try {
      const result = await awardRewards(
        currentUser.uid,
        activityType,
        metadata,
        addTCoins
      );

      return result;
    } catch (error) {
      console.error('Error awarding activity:', error);
      return { success: false, error: error.message };
    }
  };

  // Listen for award activity events
  useEffect(() => {
    const handleAwardActivity = async (event) => {
      const { activityType, metadata } = event.detail;
      await awardActivity(activityType, metadata);
    };

    window.addEventListener('awardActivity', handleAwardActivity);
    return () => {
      window.removeEventListener('awardActivity', handleAwardActivity);
    };
  }, [currentUser?.uid, addTCoins]);

  // Load user gamification stats
  useEffect(() => {
    if (!currentUser?.uid) {
      setStats({
        totalPoints: 0,
        currentXP: 0,
        totalXP: 0,
        level: 1,
        mascot: null,
        stats: {}
      });
      setLoading(false);
      return;
    }

    // Real-time listener for user document
    const userRef = doc(db, 'users', currentUser.uid);
    const unsubscribe = onSnapshot(
      userRef,
      async (snapshot) => {
        if (snapshot.exists()) {
          const userData = snapshot.data();
          const currentXP = userData.currentXP || 0;
          const mascot = getMascotByXP(currentXP);
          const newLevel = mascot?.level || 1;
          const oldLevel = stats.level || 1;

          // Check for level up
          if (newLevel > oldLevel && oldLevel > 0 && stats.currentXP > 0) {
            const levelRewards = {
              2: { coins: 100, points: 100, badge: 'Level 2 Badge' },
              3: { coins: 200, points: 200, badge: 'Level 3 Badge' },
              4: { coins: 300, points: 300, badge: 'Level 4 Badge' },
              5: { coins: 500, points: 500, badge: 'Level 5 Badge' }
            };

            setLevelUpInfo({
              oldLevel,
              newLevel,
              rewards: levelRewards[newLevel] || {}
            });
          }

          setStats({
            totalPoints: userData.totalPoints || 0,
            currentXP: currentXP,
            totalXP: userData.totalXP || 0,
            level: newLevel,
            mascot: mascot,
            stats: userData.stats || {}
          });

          // Load badges and achievements
          setBadges(userData.badges || []);
          setAchievements(userData.achievements || []);
        } else {
          setStats({
            totalPoints: 0,
            currentXP: 0,
            totalXP: 0,
            level: 1,
            mascot: null,
            stats: {}
          });
        }
        setLoading(false);
      },
      (err) => {
        console.error('Error loading gamification stats:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser?.uid]);

  /**
   * Refresh stats manually
   */
  const refreshStats = async () => {
    if (!currentUser?.uid) return;

    try {
      const newStats = await getUserGamificationStats(currentUser.uid);
      if (newStats) {
        setStats(newStats);
      }
    } catch (error) {
      console.error('Error refreshing stats:', error);
    }
  };

  const clearLevelUpInfo = () => {
    setLevelUpInfo(null);
  };

  const value = {
    stats,
    badges,
    achievements,
    levelUpInfo,
    loading,
    error,
    awardActivity,
    refreshStats,
    clearLevelUpInfo,
    ACTIVITY_TYPES
  };

  return (
    <GamificationContext.Provider value={value}>
      {children}
    </GamificationContext.Provider>
  );
};

